import React from "react";
import {
  BundleModuleContent,
  FormattedBundle,
} from "@/modules/BundleModuleContent";
import { LiquidProduct, liquidToShopifyProduct } from "@/liquid";
import { catalogSelect, droppsCatalog, productSelect } from "@/store";
import { calculateBundleValue } from "@/helpers/utils";
import { getEffectiveSubscriptionDiscountPercent } from "@/helpers/constants";

interface UnformattedProduct {
  product: LiquidProduct;
}

interface UnformattedBundle {
  title: string;
  description: string;
  image: FormattedBundle["image"];
  products: UnformattedProduct[];
  spotDiscountCode: string;
  spotDiscountPercent: number;
  subDiscountCode: string;
  subDiscountPercent: number;
  subDisplayDiscountPercent: number;
}

const formatBundle = (bundle: UnformattedBundle): FormattedBundle => {
  const catalog = droppsCatalog(
    bundle.products.map((p) => {
      return liquidToShopifyProduct(p.product);
    })
  );

  const bundleVariant = productSelect.defaultVariant;
  const allVariants = catalog.all.map(bundleVariant);
  const { retailValue, subscriptionRetailValue } =
    calculateBundleValue(allVariants);

  // Bundle variants that may allow subscriptions
  const subscriptionVariants = new Set(
    allVariants.filter((x) => x.subscription).map((x) => x.id)
  );

  // Filter variants to orderable variants where the subscription is orderable
  // (if originally present, as we allow spots on subscribeable bundles).
  const orderables = catalogSelect
    .orderable(catalog)
    .map(bundleVariant)
    .filter((x) => !subscriptionVariants.has(x.id) || x.subscription);

  // We know something is out of stock if we're looking at a variant id subset
  const orderableIds = new Set(orderables.map((x) => x.id));
  const OOSItems = allVariants.some((x) => !orderableIds.has(x.id));

  const containsSubscription = bundle.products.find(
    (x) => !!x.product.selling_plan_groups?.length
  );
  const subscription = containsSubscription && {
    price: subscriptionRetailValue.mul(
      (100 -
        getEffectiveSubscriptionDiscountPercent(bundle.subDiscountPercent, {
          isBundle: true,
        })) /
        100
    ),
    comparePrice: retailValue,
    discountText: `${getEffectiveSubscriptionDiscountPercent(
      bundle.subDisplayDiscountPercent,
      { isBundle: true }
    )}% OFF!`,
    cta: `Subscribe and Save ${getEffectiveSubscriptionDiscountPercent(
      bundle.subDisplayDiscountPercent,
      { isBundle: true }
    )}%*`,
    disclaimer: `*${getEffectiveSubscriptionDiscountPercent(
      bundle.subDisplayDiscountPercent,
      { isBundle: true }
    )}% off your first purchase. Subscriptions renew at 25% discount.`,
    subDiscountCode: bundle.subDiscountCode,
  };

  return {
    ...bundle,
    discountText: `${getEffectiveSubscriptionDiscountPercent(
      bundle.spotDiscountPercent,
      { isBundle: true }
    )}% OFF!`,
    price: retailValue.mul(
      (100 -
        getEffectiveSubscriptionDiscountPercent(bundle.spotDiscountPercent, {
          isBundle: true,
        })) /
        100
    ),
    comparePrice: retailValue,
    products: catalogSelect.orderable(catalog),
    OOSItems,
    subscription,
  };
};

export interface BundleModuleProps {
  className?: string;
  layout: "image-left" | "image-right";
  bundle: UnformattedBundle;
}

export const BundleModule = (props: BundleModuleProps) => {
  const { bundle: unformattedBundle } = props;
  const bundle = formatBundle(unformattedBundle);
  return <BundleModuleContent {...props} bundle={bundle} />;
};
